import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DeviceCommand } from '@cloudsign/shared';
import type { Device, RemoteActionRequest } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { ErrorLine } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, ConfirmButton, TableWrap, TextField, Th } from '../../components/ui';
import { BASE, CommandList, keys } from './api';

function commandState(c: DeviceCommand): { text: string; tone: 'neutral' | 'good' | 'bad' | 'warn' } {
  if (c.result === 'ok') return { text: t('devicesUi.commands.ok'), tone: 'good' };
  if (c.result === 'failed') return { text: t('devicesUi.commands.failed'), tone: 'bad' };
  if (new Date(c.expiresAt).getTime() < Date.now()) return { text: t('devicesUi.commands.expired'), tone: 'neutral' };
  return { text: t('devicesUi.commands.waiting'), tone: 'warn' };
}

/** Remote actions (devices.remote_actions). Unpair also needs sites.manage_devices and asks first. */
export function ActionsCard({ device, canUnpair }: { device: Device; canUnpair: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [seconds, setSeconds] = useState('60');
  const commands = useQuery({ queryKey: keys.commands(device.id), queryFn: () => api.get(`${BASE}/${device.id}/commands`, { schema: CommandList }) });
  const send = useMutation({
    mutationFn: (body: RemoteActionRequest) => api.post(`${BASE}/${device.id}/actions`, { body, schema: DeviceCommand }),
    onSuccess: async (_c, body) => {
      toast(t('devicesUi.action.sent', { action: t(`devicesUi.action.${body.action}`) }));
      if (body.action === 'show_message') setMessage('');
      await qc.invalidateQueries({ queryKey: ['devices'] });
      if (body.action === 'unpair') void navigate('/manage/devices');
    },
  });
  const simple = ['reload_config', 'restart_app', 'test_print'] as const;
  const submitMessage = (e: FormEvent) => {
    e.preventDefault();
    send.mutate({ action: 'show_message', message, seconds: Number(seconds) });
  };

  return (
    <Card title={t('devicesUi.actions.title')} description={t('devicesUi.actions.description')} className="mb-6">
      <ErrorLine error={send.error} />
      <div className="mt-2 flex flex-wrap gap-2">
        {simple.map((a) => (
          <Button key={a} disabled={send.isPending} onClick={() => send.mutate({ action: a })}>
            {t(`devicesUi.action.${a}`)}
          </Button>
        ))}
      </div>
      <form onSubmit={submitMessage} className="mt-5 grid gap-3 sm:grid-cols-[1fr_10rem_auto] sm:items-end">
        <TextField label={t('devicesUi.action.messageLabel')} hint={t('devicesUi.action.messageHint')} maxLength={200} value={message} onChange={(e) => setMessage(e.target.value)} />
        <TextField label={t('devicesUi.action.secondsLabel')} type="number" min={5} max={3600} value={seconds} onChange={(e) => setSeconds(e.target.value)} />
        <Button type="submit" disabled={send.isPending || message.trim() === ''}>
          {t('devicesUi.action.show_message')}
        </Button>
      </form>
      {canUnpair ? (
        <div className="mt-5 border-t border-slate-200 pt-4">
          <p className="mb-2 text-sm text-slate-600">{t('devicesUi.action.unpairHint')}</p>
          <ConfirmButton
            label={`${t('devicesUi.action.unpair')} ${device.name}`}
            confirmLabel={t('devicesUi.unpair.confirm', { name: device.name })}
            pending={send.isPending}
            onConfirm={() => send.mutate({ action: 'unpair' })}
          />
        </div>
      ) : null}
      <h3 className="mb-2 mt-6 text-sm font-semibold text-slate-900">{t('devicesUi.commands.title')}</h3>
      {commands.data && commands.data.length > 0 ? (
        <TableWrap caption={t('devicesUi.commands.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('devicesUi.commands.col.action')}</Th>
              <Th>{t('devicesUi.commands.col.sent')}</Th>
              <Th>{t('devicesUi.commands.col.result')}</Th>
            </tr>
          </thead>
          <tbody>
            {commands.data.map((c) => {
              const s = commandState(c);
              return (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5">{t(`devicesUi.action.${c.action}`)}</td>
                  <td className="px-4 py-2.5">{formatDateTime(c.createdAt)}</td>
                  <td className="px-4 py-2.5">
                    <Badge tone={s.tone}>{s.text}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
      ) : (
        <p className="text-sm text-slate-600">{t('devicesUi.commands.empty')}</p>
      )}
    </Card>
  );
}
